import { useState, useEffect, useRef } from "react";
import styles from "./FamilyInviteModal.module.css";
import { LanguageContext } from "../../../context/LanguageContext";
import Loader from "../../spinner/Loader";
import { useDispatch, useSelector } from "react-redux";
import Cookies from 'js-cookie';
import acceptDeclineInvite from "../../../store/family/acceptDeclineInvite/AcceptDeclineInvite";
import { acceptDeclineInviteAction } from "../../../store/family/acceptDeclineInvite/AcceptDeclineInviteSlice";
import { PLAN_TYPES } from "../../../utils/helpers/subscriptions/plans";
import { useNavigate } from "react-router-dom";
import getCancelData from "../../../store/subscriptions/cancel/Cancel";
import { useCurrentUser } from "../../../hooks/useCurrentUser";

function Dots({ total, current }) {
  return (
    <div className={styles.dots}>
      {Array.from({ length: total }).map((_, i) => (
        <span
          key={i}
          className={`${styles.dot} ${i === current ? styles.dotActive : ""}`}
          style={{ width: i === current ? 22 : 8 }}
        />
      ))}
    </div>
  );
}

function InviteCard({ invite, animDir, animKey, t }) {
  return (
    <div key={animKey} className={`${styles.inviteCard} ${animDir === "right" ? styles.animRight : styles.animLeft}`}>
      <span className={styles.inviteLabel}>{t("new_invitation")}</span>
      <h2 className={styles.inviteTitle}>{t("family_invitation_message")}</h2>
      <p className={styles.inviteDesc}>{t("family_invitation_description")}</p>
    </div>
  );
}

export default function FamilyInviteModal({ onClose, getInvitesData, allowReject = true }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const acceptDeclineInviteData = useSelector((state) => state.acceptDeclineInvite);
  const token = Cookies.get('auth_token');
  const [index, setIndex] = useState(0);
  const [animDir, setAnimDir] = useState("right");
  const [animKey, setAnimKey] = useState(0);
  const [dismissed, setDismissed] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [action, setAction] = useState({ type: "", id: "" });
  const overlayRef = useRef(null);
  const { user, setUser } = useCurrentUser();

  const invitations = getInvitesData?.filter((inv) => !dismissed.includes(inv._id));
  const invite = invitations?.[index] ?? invitations?.[invitations?.length - 1];
  const total = invitations?.length;

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowRight") advance(1);
      if (e.key === "ArrowLeft") advance(-1);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  });

  useEffect(() => {
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, []);

  function advance(dir) {
    setAnimDir(dir > 0 ? "right" : "left");
    setAnimKey((k) => k + 1);
    setIndex((i) => Math.max(0, Math.min(total - 1, i + dir)));
  }

  function handleCancelPlanAndJoin() {
    if (!invite) return;
    setLoading(true)
    setAction({ type: "cancel_and_accept", id: invite?._id })
    dispatch(acceptDeclineInvite(token, invite?._id, "accept"))
  }

  function handleConfirmJoin() {
    if (!invite) return;
    setLoading(true)
    setAction({ type: "accept", id: invite?._id })
    dispatch(acceptDeclineInvite(token, invite?._id, "accept"))
  }

  function handleRejectAction() {
    if (!invite) return;
    setLoading(true)
    setAction({ type: "decline", id: invite?._id })
    dispatch(acceptDeclineInvite(token, invite?._id, "decline"))
  }

  useEffect(() => {
    if (acceptDeclineInviteData?.status === "succeeded") {
      setLoading(false)
      setError(null)
      if (action.type === "accept") {
        setAction({ type: "", id: "" })
        setUser((prev) => ({ ...prev, plan: PLAN_TYPES.FAMILY }));
        navigate("/subscription")
        onClose();
      } else if (action.type === "decline") {
        setAction({ type: "", id: "" })
        const newDismissed = [...dismissed, action.id];
        const remaining = getInvitesData?.filter((inv) => !newDismissed.includes(inv._id));
        
        if (remaining.length === 0) return onClose?.()
          
        setDismissed(newDismissed);
        setAnimDir("right");
        setAnimKey((k) => k + 1);
        setIndex((i) => Math.min(i, remaining.length - 1));
      } else if (action.type === "cancel_and_accept") {
        setAction({ type: "", id: "" })
        setUser((prev) => ({ ...prev, plan: PLAN_TYPES.FAMILY }));
        navigate("/subscription")
        dispatch(getCancelData(token))
        onClose();
      }
      dispatch(acceptDeclineInviteAction.acceptDeclineInviteReset());
    } else if (acceptDeclineInviteData?.status === "failed") {
      setAction({ type: "", id: "" })
      setLoading(false)
      setError("unknown_error");
      dispatch(acceptDeclineInviteAction.acceptDeclineInviteReset());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, acceptDeclineInviteData, onClose, setUser, action, navigate]);

  return (
    <LanguageContext.Consumer>
      {({ data, language: lang }) => {
        const t = (key) => data?.[key]?.[lang];

        return (
          (!data || !lang || getInvitesData === null) ? (
            <div className={styles.overlay}>
              <div className={styles.loading}>
                <Loader />
              </div>
            </div>
          ) : (
            <div className={styles.overlay} ref={overlayRef}>
              {total > 1 && (
                <>
                  <div className={styles.stackBack1} />
                  <div className={styles.stackBack2} />
                </>
              )}

              <div className={styles.modal}>
                <div className={styles.hero}>
                  {total > 1 && (
                    <span className={styles.countBadge}>
                      {index + 1} / {total}
                    </span>
                  )}
                  <img src="/assets/icons/vinni.svg" alt="" className={styles.monsterImg} />

                  <button className={styles.closeBtn} onClick={onClose} aria-label="Close">
                    <img src="/assets/subscriptions/close.svg" alt="" width={16} height={16} />
                  </button>
                </div>

                <div className={styles.body}>
                  <InviteCard invite={invite} animDir={animDir} animKey={animKey} t={t} />
                  {total > 1 ? (
                    <Dots total={total} current={index} />
                  ) : (
                    <div className={styles.dotSpacer} />
                  )}

                  <div className={styles.inviterChip}>
                    <div className={styles.inviterAvatar}>
                      {(invite?.ownerId?.displayName || invite?.ownerId?.email || invite?.ownerId?.username || "")[0]?.toUpperCase()}
                     </div>
                    <span className={styles.inviterText}>
                      {t("invited_by")}{" "}
                      <strong className={styles.inviterEmail}>
                        {invite?.ownerId?.email ?? invite?.ownerId?.phoneNumber ?? invite?.ownerId?.username}
                      </strong>
                    </span>
                  </div>

                  {error && (
                    <p className={styles.errorMessage}>{t(error)}</p>
                  )}

                  <button className={styles.joinBtn} onClick={handleConfirmJoin} disabled={loading}>
                    {t("join_family")}
                  </button>
                  {user?.plan !== PLAN_TYPES.FREE && (
                    <button className={styles.joinBtn} onClick={handleCancelPlanAndJoin} disabled={loading}>
                      {t("cancel_plan_and_join")}
                    </button>
                  )}
                  {allowReject && (
                    <button className={styles.rejectBtn} onClick={handleRejectAction} disabled={loading}>
                      {t("reject")}
                    </button>
                  )}
                  {total > 1 && (
                    <div className={styles.navRow}>
                      <button className={styles.navBtn} onClick={() => advance(-1)} disabled={index === 0}>
                        {`← ${t("previous")}`}
                      </button>
                      <button className={styles.navBtn} onClick={() => advance(1)} disabled={index === total - 1}>
                        {`${t("next")} →`}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        );
      }}
    </LanguageContext.Consumer>
  )
}